import { useMemo } from 'react';

import mime from 'mime';
import { Accept } from 'react-dropzone';

import { useTranslation } from '../i18n';

const MAP_MIME_TYPE_TO_TRANSLATION_KEY: { [key: string]: string } = {
  'image/*': 'MIME_IMAGES',
  'video/*': 'MIME_VIDEOS',
  'audio/*': 'MIME_AUDIO',
  'application/*': 'MIME_APPLICATIONS',
  'font/*': 'MIME_FONTS',
  'text/*': 'MIME_TEXT',
} as const;

type UseSupportedFileTypesInput = {
  accept?: Accept;
};

type UseSupportedFileTypesOutput = string | undefined;

// we want to place generic mime-types first
const sortMimeTypesBySpecificity = (mimeTypes: string[]) =>
  [...mimeTypes].sort((a, b) => {
    if (a.includes('/*') && b.includes('/*')) {
      return 0;
    }

    if (a.includes('/*')) {
      return -1;
    }

    if (b.includes('/*')) {
      return 1;
    }

    return 0;
  });

export const useSupportedFileTypesDescription = ({
  accept,
}: UseSupportedFileTypesInput): UseSupportedFileTypesOutput => {
  const { t } = useTranslation();

  const supportedFileTypesDescription = useMemo(() => {
    const description: string[] = [];

    // If no accept is provided, we assume all file types are supported
    if (!accept) {
      return undefined;
    }

    const sortedMimeTypes = sortMimeTypesBySpecificity(Object.keys(accept));

    sortedMimeTypes.forEach((key) => {
      const possibleTranslationKey = MAP_MIME_TYPE_TO_TRANSLATION_KEY[key];

      if (possibleTranslationKey) {
        // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array
        description.push(t(possibleTranslationKey) as string);
        return;
      }

      const extensions = accept[key];

      if (extensions.length === 0) {
        const possibleExtension = mime.getExtension(key);
        if (possibleExtension) {
          // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array
          description.push(possibleExtension);
        }
      } else if (extensions.length > 0) {
        extensions.forEach((ext) => {
          // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array
          description.push(ext);
        });
      }
    });

    // If the description is too long, we add a generic "and more" at the end
    if (description.length > 5) {
      return `${description.slice(0, 5).join(', ')} ${t('MIME_AND_MORE')}`;
    }

    if (description.length === 1) {
      // if we only have one element, we return it
      return description.join();
    }

    // if we have more than one element, we join all but the last one with a comma and the last one with "and"
    const lastElement = description.pop();
    return `${description.join(', ')} ${t('MIME_AND')} ${lastElement}`; // i.e. "images, videos and pdf"
  }, [accept, t]);

  return supportedFileTypesDescription;
};
